import React from 'react';

export const AccommodationsSection = ({
  accommodations, stallsRequested, rvRequested, stallCost, rvCost,
  stallPricing = 'weekend', rvPricing = 'weekend', showDays = 1,
  onSetStalls, onSetRv, onSetStallPricing, onSetRvPricing
}) => {
  if (!accommodations) return null;
  const { stalls, rv_hookups } = accommodations;
  // When a producer has uploaded a stall/RV chart, riders pick specific
  // spots on the map via SpotPicker — the quantity-counter UI is redundant.
  const stallsAvail = stalls?.total > 0 && !accommodations.has_stall_chart;
  const rvAvail = rv_hookups?.total > 0 && !accommodations.has_rv_chart;
  if (!stallsAvail && !rvAvail) return null;

  const hasStallPerDay = stalls?.price_per_day > 0;
  const hasRvPerDay = rv_hookups?.price_per_day > 0;

  return (
    <div className="lg:col-span-2 bg-[#161616] border border-[#262626] rounded-lg p-5">
      <h3 className="text-lg font-bold text-white mb-4" style={{ fontFamily: 'Outfit' }}>Stalls & RV Hookups</h3>
      <div className="space-y-5">
        {stallsAvail && (
          <div data-testid="stall-section">
            <div className="flex items-center justify-between mb-2">
              <div>
                <label className="text-sm text-[#9E9E9E]">Stalls</label>
                <p className="text-[10px] text-[#555]">{stalls.remaining} of {stalls.total} remaining</p>
              </div>
              <div className="text-right">
                {stallsRequested > 0 && <span className="text-[#0096FF] text-sm font-semibold">${stallCost.toFixed(2)}</span>}
              </div>
            </div>

            {/* Pricing toggle */}
            {hasStallPerDay && stalls.price > 0 && (
              <div className="flex gap-2 mb-2" data-testid="stall-pricing-toggle">
                <button
                  onClick={() => onSetStallPricing?.('weekend')}
                  className={`flex-1 px-3 py-2 rounded-md text-xs font-semibold transition-all ${
                    stallPricing === 'weekend' ? 'bg-[#0096FF] text-white' : 'bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E]'
                  }`}
                  data-testid="stall-pricing-weekend"
                >
                  Weekend ${stalls.price.toFixed(2)}
                </button>
                <button
                  onClick={() => onSetStallPricing?.('per_day')}
                  className={`flex-1 px-3 py-2 rounded-md text-xs font-semibold transition-all ${
                    stallPricing === 'per_day' ? 'bg-[#0096FF] text-white' : 'bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E]'
                  }`}
                  data-testid="stall-pricing-perday"
                >
                  Per Day ${stalls.price_per_day.toFixed(2)}/day {showDays > 1 && <span className="text-[10px] opacity-70">({showDays}d)</span>}
                </button>
              </div>
            )}
            {hasStallPerDay && !stalls.price && (
              <p className="text-[10px] text-[#9E9E9E] mb-2">${stalls.price_per_day.toFixed(2)}/day ({showDays} day{showDays > 1 ? 's' : ''})</p>
            )}
            {!hasStallPerDay && stalls.price > 0 && (
              <p className="text-[10px] text-[#9E9E9E] mb-2">${stalls.price.toFixed(2)} per stall</p>
            )}

            <div className="flex items-center gap-3">
              <button
                onClick={() => onSetStalls(Math.max(0, stallsRequested - 1))}
                disabled={stallsRequested <= 0}
                className="w-8 h-8 rounded-md bg-[#0A0A0A] border border-[#262626] text-white font-bold text-sm disabled:opacity-30"
                data-testid="stall-decrease"
              >-</button>
              <span className="text-white font-bold text-lg w-8 text-center" data-testid="stall-count">{stallsRequested}</span>
              <button
                onClick={() => onSetStalls(Math.min(stalls.remaining, stallsRequested + 1))}
                disabled={stallsRequested >= stalls.remaining}
                className="w-8 h-8 rounded-md bg-[#0A0A0A] border border-[#262626] text-white font-bold text-sm disabled:opacity-30"
                data-testid="stall-increase"
              >+</button>
            </div>
          </div>
        )}

        {rvAvail && (
          <div data-testid="rv-section">
            <div className="flex items-center justify-between mb-2">
              <div>
                <label className="text-sm text-[#9E9E9E]">RV Hookups</label>
                <p className="text-[10px] text-[#555]">{rv_hookups.remaining} of {rv_hookups.total} remaining</p>
              </div>
              <div className="text-right">
                {rvRequested > 0 && <span className="text-[#0096FF] text-sm font-semibold">${rvCost.toFixed(2)}</span>}
              </div>
            </div>

            {/* Pricing toggle */}
            {hasRvPerDay && rv_hookups.price > 0 && (
              <div className="flex gap-2 mb-2" data-testid="rv-pricing-toggle">
                <button
                  onClick={() => onSetRvPricing?.('weekend')}
                  className={`flex-1 px-3 py-2 rounded-md text-xs font-semibold transition-all ${
                    rvPricing === 'weekend' ? 'bg-[#0096FF] text-white' : 'bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E]'
                  }`}
                  data-testid="rv-pricing-weekend"
                >
                  Weekend ${rv_hookups.price.toFixed(2)}
                </button>
                <button
                  onClick={() => onSetRvPricing?.('per_day')}
                  className={`flex-1 px-3 py-2 rounded-md text-xs font-semibold transition-all ${
                    rvPricing === 'per_day' ? 'bg-[#0096FF] text-white' : 'bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E]'
                  }`}
                  data-testid="rv-pricing-perday"
                >
                  Per Day ${rv_hookups.price_per_day.toFixed(2)}/day {showDays > 1 && <span className="text-[10px] opacity-70">({showDays}d)</span>}
                </button>
              </div>
            )}
            {hasRvPerDay && !rv_hookups.price && (
              <p className="text-[10px] text-[#9E9E9E] mb-2">${rv_hookups.price_per_day.toFixed(2)}/day ({showDays} day{showDays > 1 ? 's' : ''})</p>
            )}
            {!hasRvPerDay && rv_hookups.price > 0 && (
              <p className="text-[10px] text-[#9E9E9E] mb-2">${rv_hookups.price.toFixed(2)} per hookup</p>
            )}

            <div className="flex items-center gap-3">
              <button
                onClick={() => onSetRv(Math.max(0, rvRequested - 1))}
                disabled={rvRequested <= 0}
                className="w-8 h-8 rounded-md bg-[#0A0A0A] border border-[#262626] text-white font-bold text-sm disabled:opacity-30"
                data-testid="rv-decrease"
              >-</button>
              <span className="text-white font-bold text-lg w-8 text-center" data-testid="rv-count">{rvRequested}</span>
              <button
                onClick={() => onSetRv(Math.min(rv_hookups.remaining, rvRequested + 1))}
                disabled={rvRequested >= rv_hookups.remaining}
                className="w-8 h-8 rounded-md bg-[#0A0A0A] border border-[#262626] text-white font-bold text-sm disabled:opacity-30"
                data-testid="rv-increase"
              >+</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
